import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFirestore } from '../contexts/FirestoreContext';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Card, Form, Input, Button, Tabs, message, Alert, Tooltip } from 'antd';
import { SaveOutlined, InfoCircleOutlined, MessageOutlined, SettingOutlined } from '@ant-design/icons';
import PageHeader from '../components/PageHeader';
import SmsTemplateManager from '../components/SmsTemplateManager';

const { TextArea } = Input;
const { TabPane } = Tabs;

const OrgSettings = () => {
  const { currentUser } = useAuth();
  const { db } = useFirestore();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [orgSettings, setOrgSettings] = useState(null);

  // Check if user has CMT permissions
  const isCMTMember = () => {
    if (!currentUser) return false;
    
    const cmtRoles = [
      'cmt_leader',
      'cmt_admin', 
      'cmt_member',
      'crisis_manager',
      'safety_officer',
      'comms_media',
      'legal_compliance'
    ];
    
    return cmtRoles.includes(currentUser.role) || currentUser.isAdmin;
  };

  // Load organization settings
  useEffect(() => {
    const loadOrgSettings = async () => {
      if (!currentUser?.orgId || !db) return;

      try {
        setLoading(true);
        const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
        const orgDoc = await getDoc(orgRef);
        
        if (orgDoc.exists()) {
          const orgData = orgDoc.data();
          const settings = {
            cmtSmsEnabled: orgData.cmtSmsEnabled !== false, // Default to true
            cmtSmsTemplate: orgData.cmtSmsTemplate || "{organisation_name} Crisis Team activation: Your CMT has been activated in response to a crisis. Stand by for further details via email/Teams."
          };
          
          setOrgSettings(settings);
          form.setFieldsValue({
            cmtSmsEnabled: settings.cmtSmsEnabled,
            cmtSmsTemplate: settings.cmtSmsTemplate
          });
        }
      } catch (error) {
        console.error('Error loading organization settings:', error);
        message.error('Failed to load organization settings');
      } finally {
        setLoading(false);
      }
    };

    loadOrgSettings();
  }, [currentUser?.orgId, db, form]);

  // Save settings
  const handleSave = async (values) => {
    if (!currentUser?.orgId || !db) return;

    try {
      setSaving(true);
      const orgRef = doc(db, 'c3_organisations', currentUser.orgId);
      
      await updateDoc(orgRef, {
        cmtSmsEnabled: values.cmtSmsEnabled,
        cmtSmsTemplate: values.cmtSmsTemplate,
        cmtSmsLastUpdatedBy: currentUser.uid,
        cmtSmsLastUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setOrgSettings(values);
      message.success('Settings saved successfully');
      
    } catch (error) {
      console.error('Error saving settings:', error);
      message.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Preview SMS message
  const getPreviewMessage = () => {
    const template = form.getFieldValue('cmtSmsTemplate') || '';
    return template
      .replace(/{incident_title}/g, 'Building Fire - Level 5')
      .replace(/{incident_type}/g, 'Fire Emergency')
      .replace(/{incident_name}/g, 'Building Fire - Level 5')
      .replace(/{location}/g, 'Sydney Office Building')
      .replace(/{severity}/g, 'High')
      .replace(/{status}/g, 'Active');
  };

  // Redirect if user doesn't have permission
  if (!isCMTMember()) {
    return (
      <div className="p-4">
        <PageHeader title="Organisation Settings" />
        <Alert
          message="Access Denied"
          description="You don't have permission to access organisation settings. This area is restricted to Crisis Management Team members."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-4">
      <style>{`
        .prominent-tabs .ant-tabs-tab {
          border: 2px solid #39325b !important;
          margin: 0 4px !important;
          border-radius: 6px !important;
          background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
          box-shadow: 0 2px 4px rgba(57, 50, 91, 0.1) !important;
          transition: all 0.3s ease !important;
        }
        
        .prominent-tabs .ant-tabs-tab:hover {
          border-color: #39325b !important;
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
          box-shadow: 0 4px 8px rgba(57, 50, 91, 0.2) !important;
          transform: translateY(-1px) !important;
        }
        
        .prominent-tabs .ant-tabs-tab-active {
          border: 3px solid #39325b !important;
          background: linear-gradient(135deg, #39325b 0%, #4a3f6b 100%) !important;
          color: white !important;
          font-weight: 700 !important;
          box-shadow: 0 6px 12px rgba(57, 50, 91, 0.3) !important;
        }
        
        .prominent-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: white !important;
        }
        
        .prominent-tabs .ant-tabs-tab-active .ant-tabs-tab-btn span {
          color: white !important;
        }
        
        .prominent-tabs .ant-tabs-content-holder {
          border: 2px solid #d9d9d9;
          border-radius: 6px;
          background: white;
          padding: 20px;
          margin-top: 8px;
        }
      `}</style>
      <PageHeader title="Organisation Settings" />
      
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Alert
          message="Organisation-Wide Settings"
          description="Changes made here apply to all future crisis incidents for your organisation."
          type="info"
          showIcon
          style={{ marginBottom: '24px' }}
        />

        <Tabs 
          defaultActiveKey="communications" 
          type="card"
          size="large"
          style={{
            backgroundColor: '#f5f5f5',
            padding: '12px',
            borderRadius: '8px',
            marginBottom: '24px',
            border: '2px solid #d9d9d9'
          }}
          tabBarStyle={{
            marginBottom: '16px',
            border: 'none'
          }}
          className="prominent-tabs"
        >
                    <TabPane 
            tab={
              <span style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontWeight: '600',
                fontSize: '14px',
                padding: '4px 8px'
              }}>
                <MessageOutlined style={{ fontSize: '16px', color: '#39325b' }} />
                Crisis Communications
              </span>
            }
            key="communications"
          >
            <Card title="CMT SMS Alert Settings" loading={loading}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSave}
                initialValues={orgSettings}
              >
                <Form.Item
                  name="cmtSmsEnabled"
                  valuePropName="checked"
                  label="Enable CMT SMS Alerts"
                >
                  <Button
                    type={form.getFieldValue('cmtSmsEnabled') ? 'primary' : 'default'}
                    onClick={() => {
                      const currentValue = form.getFieldValue('cmtSmsEnabled');
                      form.setFieldsValue({ cmtSmsEnabled: !currentValue });
                    }}
                    style={{ marginBottom: '8px' }}
                  >
                    {form.getFieldValue('cmtSmsEnabled') ? 'SMS Alerts Enabled' : 'SMS Alerts Disabled'}
                  </Button>
                  <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                    When enabled, all CMT members will receive SMS alerts when new crisis incidents are created
                  </div>
                </Form.Item>

                <Form.Item
                  name="cmtSmsTemplate"
                  label={
                    <span>
                      SMS Message Template 
                      <Tooltip title="Available variables: {organisation_name}, {incident_title}, {incident_type}, {incident_name}, {location}, {severity}, {status}">
                        <InfoCircleOutlined style={{ marginLeft: '4px', color: '#1890ff' }} />
                      </Tooltip>
                    </span>
                  }
                  rules={[
                    { required: true, message: 'SMS template is required' },
                    { max: 155, message: 'Template must be 155 characters or less for SMS compatibility' }
                  ]}

                >
                  <TextArea
                    rows={4}
                    placeholder="Enter your SMS template..."
                    showCount
                    maxLength={155}
                  />
                </Form.Item>

                {/* Preview Section */}
                <div style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '16px', 
                  borderRadius: '6px', 
                  marginBottom: '24px',
                  border: '1px solid #d9d9d9'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 'bold' }}>
                    📱 SMS Preview (with example data):
                  </h4>
                  <div style={{ 
                    backgroundColor: 'white', 
                    padding: '12px', 
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'monospace',
                    border: '1px solid #e6e6e6',
                    wordBreak: 'break-word'
                  }}>
                    {getPreviewMessage() || 'Enter a template above to see preview...'}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Character count: {getPreviewMessage().length}/160 (SMS limit)
                  </div>
                </div>

                {/* Available Variables Help */}
                <Card size="small" title="Available Template Variables" style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '13px' }}>
                    <div><code>{'{incident_title}'}</code> - The title/name of the incident</div>
                    <div><code>{'{incident_type}'}</code> - Type of crisis (Fire, Security, etc.)</div>
                    <div><code>{'{incident_name}'}</code> - Alternative to incident_title</div>
                    <div><code>{'{location}'}</code> - Where the incident occurred</div>
                    <div><code>{'{severity}'}</code> - Incident severity level</div>
                    <div><code>{'{status}'}</code> - Current incident status</div>
                  </div>
                </Card>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    icon={<SaveOutlined />}
                    size="large"
                    style={{ backgroundColor: '#39325b', borderColor: '#39325b' }}
                  >
                    Save SMS Settings
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          </TabPane>
          
          <TabPane 
            tab={
              <span style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                fontWeight: '600',
                fontSize: '14px',
                padding: '4px 8px'
              }}>
                <SettingOutlined style={{ fontSize: '16px', color: '#39325b' }} />
                📱 SMS Quick Templates
              </span>
            }
            key="templates"
          >
            <SmsTemplateManager />
          </TabPane>
        </Tabs>
      </div>
    </div>
  );
};

export default OrgSettings; 